import React, { useRef, useState } from 'react';
import { FormControl, FormLabel, InputGroup, InputRightElement, Input, VStack, useToast, Box, Link, Text, Stack, } from '@chakra-ui/react';
import { isAxiosError } from 'axios';
import { localise } from '../../../services/lang/lang';
import { lookupAddressToAddress, lookupAddressToSingleLine, } from '../../../services/ui/addresses';
import { getAddressByPostcode } from '../../../services/api/generated/client';
import { unwrapModel } from '../../../services/api/unwrapModel';
import AddressSearchIcon from './AddressSearchIcon';
import AddressSearchDropDown from './AddressSearchDropDown';
import useClickOutside from '../../../hooks/useClickOutside';
import FormFieldErrors from '../FormFieldErrors/FormFieldErrors';
import ConditionalTooltip from '../Tooltip/ConditionalTooltip';
import AddressManualInput from '../AddressManualInput/AddressManualInput';
import { RenderIf } from '../RenderIf/RenderIf';
const POSTCODE_MIN_VALID_LENGTH = 5;
const POSTCODE_MAX_VALID_LENGTH = 7;
const AddressSearchFormField = ({ label, postcode, isDisabled = false, setPostcode, setAddress, manual_address, handleChange, onManualEntryOpen, defaultSelected, useRow = false, error, }) => {
    const [isFieldFocused, setIsFieldFocused] = useState(false);
    const [isShowingManualEntry, setIsShowingManualEntry] = useState(false);
    const toast = useToast();
    const [optionsHidden, setOptionsHidden] = useState(true);
    const containerRef = useRef(null);
    useClickOutside(containerRef, () => setOptionsHidden(true));
    const postcodeFieldRef = useRef(null);
    const [selectedAddress, setSelectedAddress] = useState(defaultSelected);
    const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
    const [addresses, setAddresses] = useState(undefined);
    const onSearchPostcode = async (postcodeSearch) => {
        if (!postcodeSearch)
            return;
        setIsShowingManualEntry(false);
        setIsLoadingAddresses(true);
        try {
            const result = await getAddressByPostcode(postcodeSearch);
            const addressResponse = unwrapModel(result);
            setIsLoadingAddresses(false);
            setAddresses(addressResponse);
            setOptionsHidden(false);
        }
        catch (e) {
            const errorMessage = isAxiosError(e) && e.response?.data
                ? e.response.data.message
                : localise('UNKNOWN_ERROR_MESSAGE');
            toast({
                title: errorMessage,
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
        finally {
            setIsLoadingAddresses(false);
        }
    };
    const onEditAddress = () => {
        setSelectedAddress(undefined);
        if (postcodeFieldRef.current) {
            postcodeFieldRef.current.focus();
        }
    };
    const handleManualEntryOpen = () => {
        onManualEntryOpen();
        setIsShowingManualEntry(true);
    };
    const onManualAddressKeyDown = (e) => {
        if (e.code === 'Space' || e.code === 'Enter') {
            handleManualEntryOpen();
        }
    };
    const setFormattedSelectedAddress = (address) => {
        const singleLineAddress = lookupAddressToSingleLine(address);
        const formattedAddress = lookupAddressToAddress({ lookupAddress: address });
        setSelectedAddress(singleLineAddress);
        setAddress(formattedAddress);
        setAddresses(undefined);
        setOptionsHidden(true);
    };
    const handleFocus = () => {
        setIsFieldFocused(true);
        if (!addresses)
            return;
        setOptionsHidden(false);
    };
    const shouldShowTooltip = !selectedAddress &&
        optionsHidden &&
        !isShowingManualEntry &&
        postcode.replaceAll(' ', '').length >= POSTCODE_MIN_VALID_LENGTH &&
        postcode.replaceAll(' ', '').length <= POSTCODE_MAX_VALID_LENGTH &&
        !isLoadingAddresses &&
        isFieldFocused;
    const stackProps = {
        direction: useRow ? 'row' : 'column',
        alignItems: useRow ? 'center' : 'flex-start',
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(VStack, { w: "100%", align: "flex-start" },
            React.createElement(FormControl, { isInvalid: !!error, isDisabled: isDisabled },
                React.createElement(Stack, { ...stackProps, w: "100%", "data-testid": "address_search_form_field", position: "relative", onFocus: handleFocus, onBlur: () => {
                        setIsFieldFocused(false);
                    }, ref: containerRef, gap: 0, alignSelf: "stretch" },
                    React.createElement(FormLabel, { w: "50%", "data-testid": "address_search__label" }, label),
                    React.createElement(InputGroup, null,
                        React.createElement(Input, { ref: postcodeFieldRef, value: selectedAddress || postcode, "aria-required": "true", name: "postcode", onChange: (e) => setPostcode(e.target.value), onKeyDown: (e) => {
                                if (e.key === 'Enter') {
                                    onSearchPostcode(postcode);
                                }
                            }, type: "text", placeholder: isDisabled ? undefined : localise('ENTER_POSTCODE'), w: "100%", variant: "filled", readOnly: !!selectedAddress, "data-testid": "address_search_form_field__postcode" }),
                        React.createElement(InputRightElement, { h: "100%" },
                            React.createElement(RenderIf, { condition: !isDisabled },
                                React.createElement(ConditionalTooltip, { offset: [0, -3], "aria-label": "Click to search address", label: "Click to search address", condition: true, placement: "right-end", isOpen: shouldShowTooltip },
                                    React.createElement(Box, null,
                                        React.createElement(AddressSearchIcon, { state: selectedAddress ? 'address' : 'postcode', isLoading: isLoadingAddresses, onSearch: () => onSearchPostcode(postcode), onEdit: onEditAddress, "data-testid": "address_search_icon" })))))),
                    React.createElement(AddressSearchDropDown, { addresses: addresses, isHidden: optionsHidden, onClick: setFormattedSelectedAddress }),
                    React.createElement(FormFieldErrors, { errors: error }))),
            React.createElement(RenderIf, { condition: !isDisabled },
                React.createElement(Link, { ml: useRow ? '35%' : 0, onClick: handleManualEntryOpen, onKeyDown: onManualAddressKeyDown, "data-testid": "add-inspection-modal__manual_address_link" },
                    React.createElement(Text, { tabIndex: 0, variant: "link-400-lk-3" }, localise('ADD_ADDRESS_MANUALLY'))))),
        React.createElement(RenderIf, { condition: isShowingManualEntry },
            React.createElement(AddressManualInput, { addressFields: manual_address, handleChange: handleChange, useRow: useRow }))));
};
export default AddressSearchFormField;
